/**
 * ===== BEGIN DUENDE – Swish (Wishlist King) inline-style overrides =====
 *
 * Swish injects inline `!important` styles via JavaScript on .wk-cta-button
 * (and possibly other elements). Inline !important author styles beat
 * external !important author styles regardless of selector specificity
 * (CSS spec: inline specificity = 1,0,0,0 — higher than any selector).
 * The only reliable way to override is to set our OWN inline styles
 * with !important AFTER Swish renders.
 *
 * This script:
 *   1. On DOMContentLoaded, applies overrides to existing Swish elements
 *   2. Observes #MainContent (or document.body fallback) for Swish hydrating
 *      product cards / empty state asynchronously, and re-applies overrides
 *      whenever new wk-* elements appear
 *
 * It only touches elements inside .wk-page so the header heart and
 * product-page favorites button are unaffected. Class hooks for Swish's
 * own JS (.wk-cta-button, .wk-control, .wk-content) are preserved —
 * we only mutate inline `style`, never classList or DOM structure.
 * ===== END DUENDE =====
 */

(function () {
  'use strict';

  function readVar(name, fallback) {
    var v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  }

  function applyOverrides() {
    var page = document.querySelector('.wk-page');
    if (!page) return;

    var duende25 = readVar('--duende-25', '#F5F3F0');
    var duende50 = readVar('--duende-50', '#F5F3F0');
    var duende300 = readVar('--duende-300', '#CDC9C5');
    var duende500 = readVar('--duende-500', '#6F6860');
    var duende600 = readVar('--duende-600', '#534E46');
    var duende800 = readVar('--duende-800', '#24231E');
    var duende900 = readVar('--duende-900', '#171613');

    // CTA buttons inside product cards — match theme filled button
    page.querySelectorAll('.wk-cta-button').forEach(function (btn) {
      btn.style.setProperty('background', duende900, 'important');
      btn.style.setProperty('background-color', duende900, 'important');
      btn.style.setProperty('color', duende25, 'important');
      btn.style.setProperty('border', '0.1rem solid ' + duende900, 'important');
      btn.style.setProperty('border-color', duende900, 'important');
      btn.style.setProperty('border-radius', '5rem', 'important');
    });

    // Dropdown — outlined pill (matches country-drawer trigger style).
    // Strip borders from the wrapper chain so only `.wk-content` carries
    // the visible pill border (no concentric "double border" effect).
    page.querySelectorAll('wk-option-select, .wk-option-select').forEach(function (el) {
      el.style.setProperty('border', '0', 'important');
      el.style.setProperty('outline', '0', 'important');
      el.style.setProperty('background', 'transparent', 'important');
      el.style.setProperty('background-color', 'transparent', 'important');
      el.style.setProperty('box-shadow', 'none', 'important');
    });
    page.querySelectorAll('.wk-control').forEach(function (el) {
      el.style.setProperty('border', '0', 'important');
      el.style.setProperty('outline', '0', 'important');
      el.style.setProperty('background', 'transparent', 'important');
      el.style.setProperty('background-color', 'transparent', 'important');
      el.style.setProperty('box-shadow', 'none', 'important');
      el.style.setProperty('border-radius', '5rem', 'important');
      el.style.setProperty('width', '100%', 'important');
    });
    page.querySelectorAll('.wk-content').forEach(function (el) {
      el.style.setProperty('display', 'flex', 'important');
      el.style.setProperty('align-items', 'center', 'important');
      el.style.setProperty('justify-content', 'space-between', 'important');
      el.style.setProperty('border-radius', '5rem', 'important');
      el.style.setProperty('border', '0.1rem solid ' + duende900, 'important');
      el.style.setProperty('border-color', duende900, 'important');
      el.style.setProperty('background', 'transparent', 'important');
      el.style.setProperty('background-color', 'transparent', 'important');
      el.style.setProperty('color', duende900, 'important');
      el.style.setProperty('padding', '1.2rem 2.4rem', 'important');
      el.style.setProperty('min-height', '4.2rem', 'important');
      el.style.setProperty('font-weight', '700', 'important');
      el.style.setProperty('text-transform', 'uppercase', 'important');
      el.style.setProperty('letter-spacing', '0', 'important');
    });
    page.querySelectorAll('.wk-input').forEach(function (el) {
      el.style.setProperty('border', '0', 'important');
      el.style.setProperty('outline', '0', 'important');
      el.style.setProperty('background', 'transparent', 'important');
      el.style.setProperty('background-color', 'transparent', 'important');
      el.style.setProperty('box-shadow', 'none', 'important');
      el.style.setProperty('border-radius', '5rem', 'important');
    });

    // Price colors — compare price (struck through) muted gray;
    // current/sale price brand-dark for visual hierarchy.
    page.querySelectorAll('.wk-compare-price').forEach(function (el) {
      el.style.setProperty('color', duende500, 'important');
      el.style.setProperty('text-decoration', 'line-through', 'important');
      el.style.setProperty('font-weight', '700', 'important');
    });
    page.querySelectorAll('.wk-current-price').forEach(function (el) {
      el.style.setProperty('color', duende900, 'important');
      el.style.setProperty('text-decoration', 'none', 'important');
      el.style.setProperty('font-weight', '700', 'important');
    });

    // Heading: apply Contact/FAQ-page spec (Inktrap-Black 700 / 28px /
    // -0.01em / uppercase) inline so Swish can't override the size or
    // family from its own JS-injected styles. Color is set later per
    // empty vs filled state.
    if (title) {
      title.style.setProperty(
        'font-family',
        '"area-inktrap", "area-inktrap-black", "area-variable", system-ui, sans-serif',
        'important'
      );
      title.style.setProperty('font-weight', '700', 'important');
      title.style.setProperty('font-size', '2.8rem', 'important');
      title.style.setProperty('line-height', '1.2', 'important');
      title.style.setProperty('letter-spacing', '-0.01em', 'important');
      title.style.setProperty('text-transform', 'uppercase', 'important');
    }

    // Empty-state layout overrides — 3.2rem gaps (bigger than cart
    // drawer's 2.4rem; the wishlist heading needs more breathing room
    // because it's a single-line title instead of cart's 2-line
    // "IT'S A LITTLE / EMPTY HERE" composition).
    if (isEmpty) {
      page.style.setProperty('display', 'flex', 'important');
      page.style.setProperty('flex-direction', 'column', 'important');
      page.style.setProperty('align-items', 'center', 'important');
      page.style.setProperty('gap', '3.2rem', 'important');

      // Heading: darker ink, zero margin (gap on .wk-page handles spacing).
      if (title) {
        title.style.setProperty('color', duende800, 'important');
        title.style.setProperty('margin', '0', 'important');
      }

      // .wk-body becomes its own flex column with 3.2rem gap between
      // description and CTA. The CTA gets an extra 0.8rem margin-top
      // (set below in the buttons loop) so total description → CTA
      // spacing = 4rem.
      page.querySelectorAll('.wk-body').forEach(function (body) {
        body.style.setProperty('display', 'flex', 'important');
        body.style.setProperty('flex-direction', 'column', 'important');
        body.style.setProperty('align-items', 'center', 'important');
        body.style.setProperty('gap', '3.2rem', 'important');
        body.style.setProperty('padding', '0', 'important');
        body.style.setProperty('margin', '0', 'important');
      });

      // .wk-header reset so flex `gap` on .wk-page is the only source
      // of heading → body separation.
      page.querySelectorAll('.wk-header').forEach(function (h) {
        h.style.setProperty('margin', '0', 'important');
        h.style.setProperty('padding', '0', 'important');
      });

      // Description: body family + --duende-500. Skip dropdown labels,
      // CTA spans, price spans — anything that's not the empty message.
      page.querySelectorAll('p, span').forEach(function (el) {
        if (
          el.closest('.wk-cta-button') ||
          el.closest('.wk-option-select') ||
          el.classList.contains('wkh-text') ||
          el.classList.contains('wk-text') ||
          el.classList.contains('wk-cta-label') ||
          el.classList.contains('wk-label') ||
          el.classList.contains('wk-price') ||
          el.classList.contains('wk-current-price') ||
          el.classList.contains('wk-compare-price')
        ) return;
        el.style.setProperty('color', duende500, 'important');
        el.style.setProperty('font-size', '1.2rem', 'important');
        el.style.setProperty('line-height', '1.5', 'important');
        el.style.setProperty('margin', '0', 'important');
        el.style.setProperty('text-align', 'center', 'important');
      });

      // Empty-state CTA — outlined pill matching `.cart-drawer__empty .button`.
      // The 0.8rem margin-block-start adds onto the parent's 2.4rem gap
      // to reach 3.2rem total description → button spacing (cart spec).
      page.querySelectorAll('a, button').forEach(function (el) {
        if (
          el.classList.contains('wk-cta-button') ||
          el.closest('.wk-product-card') ||
          el.closest('.wk-controls') ||
          el.closest('wishlist-share')
        ) return;
        el.style.setProperty('background', 'transparent', 'important');
        el.style.setProperty('background-color', 'transparent', 'important');
        el.style.setProperty('color', duende900, 'important');
        el.style.setProperty('border', '0.1rem solid ' + duende900, 'important');
        el.style.setProperty('border-color', duende900, 'important');
        el.style.setProperty('border-radius', '5rem', 'important');
        el.style.setProperty('padding', '1.2rem 2.4rem', 'important');
        el.style.setProperty('text-transform', 'uppercase', 'important');
        el.style.setProperty('font-weight', '700', 'important');
        el.style.setProperty('font-size', '1.2rem', 'important');
        el.style.setProperty('letter-spacing', '0', 'important');
        el.style.setProperty('text-decoration', 'none', 'important');
        el.style.setProperty('min-height', '4.2rem', 'important');
        el.style.setProperty('display', 'inline-flex', 'important');
        el.style.setProperty('align-items', 'center', 'important');
        el.style.setProperty('justify-content', 'center', 'important');
        el.style.setProperty('margin-block-start', '0.8rem', 'important');
      });
    } else {
      // Filled state: clear any inline overrides we set during a prior
      // empty render so the base CSS rules win cleanly. The heading
      // font-family/size/weight/etc. inline overrides above stay (they
      // apply to both states); only the empty-only color + margin reset.
      page.style.removeProperty('display');
      page.style.removeProperty('flex-direction');
      page.style.removeProperty('align-items');
      page.style.removeProperty('gap');
      if (title) {
        title.style.setProperty('color', duende600, 'important');
        title.style.setProperty('margin', '0 0 6rem', 'important');
      }
    }
  }

  function init() {
    applyOverrides();

    // Re-apply when Swish hydrates / re-renders. Observe a small subtree
    // (the wishlist page wrapper if present; otherwise body until it
    // appears) and debounce via rAF so we don't thrash on every mutation.
    var queued = false;
    var observer = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        applyOverrides();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
